import { useState } from "react";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import type { DeceasedPerson } from "../../../../../../../bookings/types/edit-booking";
import StreamViewer from "../../../shared-ui/StreamViewer";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import DonationsTab from "../../../tabs/DonationTab";
import { DownloadsTab } from "../../../tabs/DownloadsTab";
import AdminTab from "../../../tabs/admin-tab/AdminTab";
import type {
  IStreamDetails,
  IThemeConfig,
} from "../../../types/memorial-types";
import HeroHeader from "./components/HeroHeader";
import Template2TabBar, { type T2Tab } from "./components/Template2TabBar";
import ObituaryTab from "../../../tabs/OrbituaryTab";
import FavouritesTab from "../../../tabs/FavouritesTab";
import TimelineTab from "../../../tabs/TimelineTab";
import ShareSidebar from "./components/ShareSidebar";
import CommentCardView from "../../../shared-ui/CommentCardView";
import DonationSideCard from "./components/DonationSideCard";
import Template2Footer from "./components/Template2Footer";

export interface FuneralTemplate2Props {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedData: DeceasedPerson | null;
  isLoadingDeceased: boolean;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;

  streamProgram: string | null;
  downloadingProgram: boolean;
  handleDownloadProgram: () => void;

  donationRefreshKey: number;
  fetchStreamDetails: () => Promise<void>;
  onDonationSuccess?: () => void;

  isStaging?: boolean;
  contactPhone?: string;
  contactEmail?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  whatsappUrl?: string;
}

export default function FuneralTemplate2({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedData,
  isLoadingDeceased,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  hasNextPage,
  onNextPage,
  donationRefreshKey,
  fetchStreamDetails,
  onDonationSuccess,
  isStaging = false,
  contactPhone,
  contactEmail,
  facebookUrl,
  linkedinUrl,
  youtubeUrl,
  whatsappUrl,
}: FuneralTemplate2Props) {
  const [tabValue, setTabValue] = useState<T2Tab>("memories");
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
      <HeroHeader stream={currentStream} deceasedData={deceasedData} />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ pt: 3, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "flex-start",
            gap: 2,
          }}
        >
          {!isMobile && <ShareSidebar streamData={currentStream} />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StreamViewer
              streams={streams}
              activeStream={activeStreamIndex}
              onStreamChange={onStreamChange}
              memorialTheme={memorialTheme}
              currentStream={currentStream}
              deceasedName={deceasedData?.first_name}
              hideTabs={streams.length <= 1}
              onDonationSuccess={onDonationSuccess}
            />
          </Box>
          {!isMobile && currentStream.chema_fund_enabled && (
            <DonationSideCard
              streamId={currentStream.id}
              streamTitle={currentStream.title}
              deceasedImage={currentStream.booking?.deceased?.[0]?.person_image}
              refreshKey={donationRefreshKey}
              onDonationSuccess={onDonationSuccess}
            />
          )}
        </Box>
      </Container>

      <Template2TabBar
        value={tabValue}
        onChange={setTabValue}
        isAdmin={isAdmin}
        donationEnabled={currentStream.chema_fund_enabled ?? false}
        accentColor={memorialTheme.primary}
      />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ py: 3 }}>
        {tabValue === "obituary" && (
          <ObituaryTab
            deceasedData={deceasedData}
            streamData={currentStream}
            isLoadingDeceased={isLoadingDeceased}
            isAdmin={isAdmin}
          />
        )}

        {tabValue === "memories" && (
          <CommentCardView
            messages={messages}
            isAdmin={isAdmin}
            socketRef={socketRef}
            isMobile={isMobile}
            handleApprove={handleApprove}
            handleDelete={handleDelete}
            hasNextPage={hasNextPage}
            onNextPage={onNextPage}
            onMessageSent={onMessageSent}
          />
        )}

        {tabValue === "favourites" && (
          <FavouritesTab
            deceasedId={deceasedData?.id}
            deceasedFirstName={deceasedData?.first_name}
            accentColor={memorialTheme.primary}
            isAdmin={isAdmin}
            variant="embedded"
          />
        )}

        {tabValue === "timeline" && (
          <TimelineTab
            deceasedId={deceasedData?.id}
            deceasedFirstName={deceasedData?.first_name}
            accentColor={memorialTheme.primary}
            isAdmin={isAdmin}
            variant="embedded"
          />
        )}

        {tabValue === "admin" &&
          (isStaging ? (
            <AdminTab />
          ) : (
            <DownloadsTab
              streamId={currentStream?.id?.toString() ?? ""}
              vimeoLink={currentStream?.vimeo_stream_link ?? ""}
              vimeoPassword={currentStream?.vimeo_download_password ?? ""}
              currentStream={currentStream}
            />
          ))}

        {tabValue === "donations" && (
          <Box sx={{ mt: 4 }}>
            <DonationsTab
              streamData={currentStream}
              streamId={currentStream?.id?.toString() ?? ""}
              isAdmin={isAdmin}
              isMobile={isMobile}
              fetchStreamDetails={fetchStreamDetails}
              donationRefreshKey={donationRefreshKey}
            />
          </Box>
        )}
      </Container>
      <Template2Footer
        memorialTheme={memorialTheme}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        facebookUrl={facebookUrl}
        linkedinUrl={linkedinUrl}
        youtubeUrl={youtubeUrl}
        whatsappUrl={whatsappUrl}
      />
    </Box>
  );
}